import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Phone, Mail, MapPin, Calendar, AlertTriangle, Edit2, Save, X, GraduationCap, Clock, CheckCircle2, XCircle, FileText, Upload, User, Trophy, Hash, Droplet, Home, Users, Eye, Trash2, ArrowUpRight, Download, UserCheck } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentProfilePage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useApp();
  const isTeacher = currentUser?.role === 'teacher';
  const isParent = currentUser?.role === 'parent';

  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Document upload
  const [docFile, setDocFile] = useState(null);
  const [docType, setDocType] = useState('birth_certificate');
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);

  // Document viewer
  const [viewingDoc, setViewingDoc] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(null);

  // Achievements
  const [isAddAchievement, setIsAddAchievement] = useState(false);
  const [achievementForm, setAchievementForm] = useState({ title: '', type: '', description: '' });

  // Transfer dialog
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({ transferType: 'internal', newClass: '', newSection: '', reason: '', destinationSchool: '' });
  const [transferring, setTransferring] = useState(false);

  // Mentor dialog
  const [mentorOpen, setMentorOpen] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState('');
  const [assigningMentor, setAssigningMentor] = useState(false);

  const allClasses = ['Nursery','LKG','UKG','Preparatory','1','2','3','4','5','6','7','8','9','10','11','12'];
  const allSections = ['A','B','C','D','E'];

  useEffect(() => {
    loadProfile();
  }, [studentId]);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const data = await api.getStudentProfile(studentId);
      setProfile(data);
    } catch (error) {
      toast.error(error.message || 'Failed to load student profile');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = () => {
    setEditForm({
      parentName: profile?.parentContact?.parentName || '',
      contactNumber: profile?.parentContact?.contactNumber || '',
      alternateContactNumber: profile?.parentContact?.alternateContactNumber || '',
      parentRelation: profile?.parentContact?.parentRelation || '',
      emergencyNotes: profile?.emergencyNotes || '',
      rollNumber: profile?.rollNumber || '',
      secondaryContactName: profile?.parentContact?.secondaryContactName || '',
      secondaryContactNumber: profile?.parentContact?.secondaryContactNumber || '',
      secondaryContactRelation: profile?.parentContact?.secondaryContactRelation || '',
      achievements: profile?.achievements || [],
      ...(isAdmin ? {
        fullName: profile?.fullName || '',
        medicalConditions: profile?.medicalConditions || '',
        address: profile?.address || '',
        bloodGroup: profile?.bloodGroup || '',
        house: profile?.house || '',
        stream: profile?.stream || '',
        studentSince: profile?.studentSince || '',
      } : {})
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.updateStudent(studentId, editForm);
      toast.success('Student profile updated');
      setIsEditing(false);
      loadProfile();
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDocUpload = async () => {
    if (!docFile) return;
    setIsUploadingDoc(true);
    try {
      await api.uploadStudentDocument(studentId, docType, docFile);
      toast.success('Document uploaded');
      setDocFile(null);
      loadProfile();
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setIsUploadingDoc(false);
    }
  };

  const handleDeleteDoc = async () => {
    if (!deletingDoc?.fileId) return;
    try {
      await api.deleteDocument(deletingDoc.fileId);
      toast.success('Document removed');
      setDeletingDoc(null);
      loadProfile();
    } catch (error) {
      toast.error(error.message || 'Failed to remove document');
    }
  };

  // Transfer
  const getToken = () => typeof window !== 'undefined' ? localStorage.getItem('sip_token') : '';
  const handleTransfer = async () => {
    setTransferring(true);
    try {
      const res = await fetch(`${API_URL}/api/students/${studentId}/transfer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(transferForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Transfer failed');
      toast.success(data.message + (data.tcNumber ? ` | TC: ${data.tcNumber}` : ''));
      setTransferOpen(false);
      loadProfile();
    } catch (e) { toast.error(e.message); }
    setTransferring(false);
  };

  // Promote individual
  const handlePromote = async () => {
    try {
      const res = await fetch(`${API_URL}/api/students/${studentId}/promote`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Promotion failed');
      toast.success(data.message);
      loadProfile();
    } catch (e) { toast.error(e.message); }
  };

  // Mentor
  const openMentorDialog = async () => {
    setMentorOpen(true);
    try {
      const data = await api.listTeachers();
      setTeachers(data || []);
      setSelectedMentor(profile?.mentorId || '');
    } catch { toast.error('Failed to load teachers'); }
  };

  const handleAssignMentor = async () => {
    if (!selectedMentor) { toast.error('Select a teacher'); return; }
    setAssigningMentor(true);
    try {
      const res = await fetch(`${API_URL}/api/students/${studentId}/mentor`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ mentorId: selectedMentor }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      setMentorOpen(false);
      loadProfile();
    } catch (e) { toast.error(e.message); }
    setAssigningMentor(false);
  };

  // Download admission form
  const downloadAdmissionForm = async () => {
    try {
      const res = await fetch(`${API_URL}/api/students/${studentId}/admission-form`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `admission_form_${profile.fullName.replace(/ /g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Admission form downloaded');
    } catch { toast.error('Failed to download form'); }
  };

  const handleAddAchievement = async () => {
    if (!achievementForm.title.trim()) {
      toast.error('Achievement title is required');
      return;
    }
    const updated = [...(profile.achievements || []), { ...achievementForm, addedAt: new Date().toISOString() }];
    try {
      await api.updateStudent(studentId, { achievements: updated });
      toast.success('Achievement added');
      setIsAddAchievement(false);
      setAchievementForm({ title: '', type: '', description: '' });
      loadProfile();
    } catch (error) {
      toast.error(error.message || 'Failed to add achievement');
    }
  };

  const handleRemoveAchievement = async (idx) => {
    const updated = (profile.achievements || []).filter((_, i) => i !== idx);
    try {
      await api.updateStudent(studentId, { achievements: updated });
      toast.success('Achievement removed');
      loadProfile();
    } catch (error) {
      toast.error(error.message || 'Failed to remove achievement');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="student-profile-loading">
        <div className="w-10 h-10 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-20" data-testid="student-profile-error">
        <p className="text-muted-foreground">Student not found or access denied.</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
      </div>
    );
  }

  const att = profile.attendanceSummary;
  const isClassEleventh = ['11', '12'].includes(profile.studentClass);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="student-profile-page">
      {/* Back + Header + Admin Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <PageHeader title="Student Profile" description={`Viewing ${profile.fullName}'s profile`} />
        </div>
        {isAdmin && profile.status === 'active' && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handlePromote} data-testid="promote-btn">
              <GraduationCap className="w-3.5 h-3.5 mr-1" /> Promote
            </Button>
            <Button size="sm" variant="outline" onClick={() => setTransferOpen(true)} data-testid="transfer-btn">
              <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> Transfer
            </Button>
            <Button size="sm" variant="outline" onClick={openMentorDialog} data-testid="mentor-btn">
              <UserCheck className="w-3.5 h-3.5 mr-1" /> Mentor
            </Button>
            <Button size="sm" variant="outline" onClick={downloadAdmissionForm} data-testid="admission-form-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Admission Form
            </Button>
          </div>
        )}
      </div>

      {/* Profile Card */}
      <DataCard>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex items-start gap-4 flex-1">
            <Avatar className="w-16 h-16">
              {profile.photoUrl ? (
                <img src={profile.photoUrl} alt={profile.fullName} className="w-full h-full object-cover rounded-full" />
              ) : (
                <AvatarFallback className="bg-brand-deep-blue text-white text-xl font-bold">
                  {profile.fullName?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="flex-1">
              <h2 className="text-xl font-bold">{profile.fullName}</h2>
              <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1"><GraduationCap className="w-4 h-4" /> Class {profile.studentClass}-{profile.section}</span>
                {profile.rollNumber && <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> Roll #{profile.rollNumber}</span>}
                {isClassEleventh && profile.stream && <Badge variant="outline">{profile.stream}</Badge>}
                <Badge className={profile.status === 'active' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-slate-200 text-slate-600'}>{profile.status}</Badge>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                {profile.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{profile.email}</span>}
                {profile.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{profile.address}</span>}
                {profile.house && <span className="flex items-center gap-1"><Home className="w-3 h-3" />{profile.house}</span>}
                {profile.studentSince && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Since {profile.studentSince}</span>}
                {profile.mentorName && <span className="flex items-center gap-1"><UserCheck className="w-3 h-3 text-brand-deep-blue" />Mentor: {profile.mentorName}</span>}
                {profile.parentId && <span className="flex items-center gap-1"><Hash className="w-3 h-3" />PID: {profile.parentId}</span>}
                {profile.tcNumber && <Badge variant="outline" className="text-orange-500 border-orange-300">TC: {profile.tcNumber}</Badge>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {(isAdmin || isTeacher) && !isEditing && (
              <Button variant="outline" onClick={startEditing} data-testid="edit-profile-btn">
                <Edit2 className="w-4 h-4 mr-2" /> Edit
              </Button>
            )}
          </div>
        </div>
      </DataCard>

      {/* Attendance Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DataCard className="text-center">
          <div className="text-3xl font-bold text-brand-deep-blue">{att?.percentage ?? 0}%</div>
          <p className="text-xs text-muted-foreground mt-1">Attendance (Class {profile.studentClass}-{profile.section})</p>
        </DataCard>
        <DataCard className="text-center">
          <div className="flex items-center justify-center gap-1 text-emerald-600"><CheckCircle2 className="w-5 h-5" /><span className="text-2xl font-bold">{att?.present ?? 0}</span></div>
          <p className="text-xs text-muted-foreground mt-1">Present</p>
        </DataCard>
        <DataCard className="text-center">
          <div className="flex items-center justify-center gap-1 text-red-500"><XCircle className="w-5 h-5" /><span className="text-2xl font-bold">{att?.absent ?? 0}</span></div>
          <p className="text-xs text-muted-foreground mt-1">Absent</p>
        </DataCard>
        <DataCard className="text-center">
          <div className="flex items-center justify-center gap-1 text-amber-500"><Clock className="w-5 h-5" /><span className="text-2xl font-bold">{att?.late ?? 0}</span></div>
          <p className="text-xs text-muted-foreground mt-1">Late</p>
        </DataCard>
      </div>

      {/* Editable Details */}
      {isEditing ? (
        <DataCard>
          <h3 className="font-semibold mb-4">Edit Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isAdmin && (
              <>
                <div><Label>Full Name</Label><Input value={editForm.fullName} onChange={e => setEditForm({...editForm, fullName: e.target.value})} data-testid="edit-fullname" /></div>
                <div><Label>Address</Label><Input value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} data-testid="edit-address" /></div>
                <div><Label>Blood Group</Label>
                  <Select value={editForm.bloodGroup || 'none'} onValueChange={v => setEditForm({...editForm, bloodGroup: v === 'none' ? '' : v})}>
                    <SelectTrigger data-testid="edit-blood-group"><SelectValue placeholder="Blood Group" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-</SelectItem>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>House</Label><Input value={editForm.house} onChange={e => setEditForm({...editForm, house: e.target.value})} data-testid="edit-house" /></div>
                {isClassEleventh && (
                  <div><Label>Stream</Label>
                    <Select value={editForm.stream || 'none'} onValueChange={v => setEditForm({...editForm, stream: v === 'none' ? '' : v})}>
                      <SelectTrigger data-testid="edit-stream"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">-</SelectItem><SelectItem value="Science">Science</SelectItem><SelectItem value="Commerce">Commerce</SelectItem><SelectItem value="Arts">Arts</SelectItem></SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>Student Since</Label><Input type="date" value={editForm.studentSince} onChange={e => setEditForm({...editForm, studentSince: e.target.value})} data-testid="edit-student-since" /></div>
                <div className="md:col-span-2"><Label>Medical Conditions</Label><Textarea value={editForm.medicalConditions} onChange={e => setEditForm({...editForm, medicalConditions: e.target.value})} rows={2} data-testid="edit-medical" /></div>
              </>
            )}
            {isAdmin && <div><Label>Roll Number</Label><Input type="number" value={editForm.rollNumber} onChange={e => setEditForm({...editForm, rollNumber: parseInt(e.target.value) || ''})} data-testid="edit-roll-number" /></div>}
            <div><Label>Parent Name</Label><Input value={editForm.parentName} onChange={e => setEditForm({...editForm, parentName: e.target.value})} data-testid="edit-parent-name" /></div>
            <div><Label>Parent Contact</Label><Input value={editForm.contactNumber} onChange={e => setEditForm({...editForm, contactNumber: e.target.value.replace(/[^0-9+\-\s]/g, '')})} data-testid="edit-parent-phone" /></div>
            <div><Label>Alternate Contact</Label><Input value={editForm.alternateContactNumber} onChange={e => setEditForm({...editForm, alternateContactNumber: e.target.value.replace(/[^0-9+\-\s]/g, '')})} data-testid="edit-alt-phone" /></div>
            <div><Label>Relation</Label><Input value={editForm.parentRelation} onChange={e => setEditForm({...editForm, parentRelation: e.target.value})} placeholder="e.g., Father, Mother, Guardian" data-testid="edit-parent-relation" /></div>
            <p className="text-xs font-medium text-muted-foreground border-b pb-1 pt-2 md:col-span-2">Secondary Contact</p>
            <div><Label>Name</Label><Input value={editForm.secondaryContactName} onChange={e => setEditForm({...editForm, secondaryContactName: e.target.value})} data-testid="edit-sec-name" /></div>
            <div><Label>Phone</Label><Input value={editForm.secondaryContactNumber} onChange={e => setEditForm({...editForm, secondaryContactNumber: e.target.value.replace(/[^0-9+\-\s]/g, '')})} data-testid="edit-sec-phone" /></div>
            <div><Label>Relation</Label><Input value={editForm.secondaryContactRelation} onChange={e => setEditForm({...editForm, secondaryContactRelation: e.target.value})} data-testid="edit-sec-relation" /></div>
            <div className="md:col-span-2"><Label>Emergency Notes</Label><Textarea value={editForm.emergencyNotes} onChange={e => setEditForm({...editForm, emergencyNotes: e.target.value})} rows={2} placeholder="Any emergency instructions..." data-testid="edit-emergency-notes" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsEditing(false)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="save-profile-btn">
              <Save className="w-4 h-4 mr-1" /> {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DataCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Medical & Emergency */}
          <DataCard>
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Heart className="w-4 h-4 text-red-500" /> Medical & Emergency</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground">Medical Conditions</p>
                <p className="font-medium">{profile.medicalConditions || 'None reported'}</p>
              </div>
              {profile.bloodGroup && (
                <div>
                  <p className="text-muted-foreground">Blood Group</p>
                  <p className="font-medium flex items-center gap-1"><Droplet className="w-3 h-3 text-red-500" /> {profile.bloodGroup}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">Emergency Notes</p>
                <p className="font-medium">{profile.emergencyNotes || 'None'}</p>
              </div>
            </div>
          </DataCard>

          {/* Parent Contact */}
          <DataCard>
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Phone className="w-4 h-4 text-blue-500" /> Parent Contact</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground">Parent Name</p>
                <p className="font-medium">{profile.parentContact?.parentName || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Relation</p>
                <p className="font-medium">{profile.parentContact?.parentRelation || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Phone</p>
                <p className="font-medium">{profile.parentContact?.contactNumber || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Alternate Phone</p>
                <p className="font-medium">{profile.parentContact?.alternateContactNumber || '-'}</p>
              </div>
              {(profile.parentContact?.secondaryContactName || profile.parentContact?.secondaryContactNumber) && (
                <>
                  <p className="text-xs font-medium text-muted-foreground border-t pt-2">Secondary Contact</p>
                  {profile.parentContact?.secondaryContactName && <div><p className="text-muted-foreground">Name</p><p className="font-medium">{profile.parentContact.secondaryContactName} {profile.parentContact.secondaryContactRelation ? `(${profile.parentContact.secondaryContactRelation})` : ''}</p></div>}
                  {profile.parentContact?.secondaryContactNumber && <div><p className="text-muted-foreground">Phone</p><p className="font-medium">{profile.parentContact.secondaryContactNumber}</p></div>}
                </>
              )}
            </div>
          </DataCard>
        </div>
      )}

      {/* Achievements */}
      <DataCard>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Achievements</h3>
          {(isAdmin || isTeacher) && (
            <Button size="sm" variant="outline" onClick={() => setIsAddAchievement(true)} data-testid="add-achievement-btn">
              + Add
            </Button>
          )}
        </div>
        {profile.achievements?.length > 0 ? (
          <div className="space-y-2">
            {profile.achievements.map((a, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg" data-testid={`achievement-${i}`}>
                <div>
                  <p className="text-sm font-medium">{a.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {a.type && <Badge variant="outline" className="text-xs">{a.type}</Badge>}
                    {a.description && <p className="text-xs text-muted-foreground">{a.description}</p>}
                  </div>
                </div>
                {(isAdmin || isTeacher) && (
                  <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleRemoveAchievement(i)} data-testid={`remove-achievement-${i}`}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No achievements recorded yet.</p>
        )}
      </DataCard>

      {/* Add Achievement Dialog */}
      <Dialog open={isAddAchievement} onOpenChange={setIsAddAchievement}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Add Achievement</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Title *</Label><Input value={achievementForm.title} onChange={e => setAchievementForm({...achievementForm, title: e.target.value})} placeholder="e.g., School Cricket Captain" data-testid="achievement-title" /></div>
            <div><Label>Type</Label>
              <Select value={achievementForm.type || 'none'} onValueChange={v => setAchievementForm({...achievementForm, type: v === 'none' ? '' : v})}>
                <SelectTrigger data-testid="achievement-type"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-</SelectItem>
                  <SelectItem value="School Team">School Team</SelectItem>
                  <SelectItem value="Extra Curricular">Extra Curricular</SelectItem>
                  <SelectItem value="Head Boy/Girl">Head Boy/Girl</SelectItem>
                  <SelectItem value="House Head">House Head</SelectItem>
                  <SelectItem value="Prefect">Prefect</SelectItem>
                  <SelectItem value="Academic">Academic</SelectItem>
                  <SelectItem value="Sports">Sports</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Input value={achievementForm.description} onChange={e => setAchievementForm({...achievementForm, description: e.target.value})} placeholder="Brief details" data-testid="achievement-desc" /></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsAddAchievement(false)}>Cancel</Button>
            <Button onClick={handleAddAchievement} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="save-achievement-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents Repository — Admin only */}
      {isAdmin && (
      <DataCard>
        <h3 className="font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-slate-500" /> Documents</h3>
        {isAdmin && (
          <div className="flex items-end gap-3 mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <div className="flex-1">
              <Label className="text-xs">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="h-9" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="birth_certificate">Birth Certificate</SelectItem>
                  <SelectItem value="transfer_cert">Transfer Certificate</SelectItem>
                  <SelectItem value="report_card">Report Card</SelectItem>
                  <SelectItem value="id_proof">ID Proof</SelectItem>
                  <SelectItem value="medical_cert">Medical Certificate</SelectItem>
                  <SelectItem value="medical_records">Medical Records</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-xs">File (<span className="text-brand-deep-blue font-semibold cursor-pointer underline" data-testid="browse-highlight">Browse</span>)</Label>
              <Input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" onChange={e => setDocFile(e.target.files[0])} className="h-9" data-testid="doc-file-input" />
            </div>
            <Button size="sm" onClick={handleDocUpload} disabled={isUploadingDoc || !docFile} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="upload-doc-btn">
              <Upload className="w-4 h-4 mr-1" /> {isUploadingDoc ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        )}
        {profile.documents?.length > 0 ? (
          <div className="space-y-2">
            {profile.documents.map((doc, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg" data-testid={`document-${i}`}>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{doc.fileName}</p>
                    <p className="text-xs text-muted-foreground capitalize">{doc.docType?.replace(/_/g, ' ')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {doc.fileUrl && (
                    <Button variant="ghost" size="sm" title="View Document" onClick={() => setViewingDoc(doc)} data-testid={`view-doc-${i}`}>
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="ghost" size="sm" title="Remove Document" onClick={() => setDeletingDoc(doc)} className="text-red-500 hover:text-red-700" data-testid={`remove-doc-${i}`}>
                      <Trash2 className="w-4 h-4 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
        )}
      </DataCard>
      )}

      {/* Document Viewer Dialog */}
      <Dialog open={!!viewingDoc} onOpenChange={() => setViewingDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> {viewingDoc?.fileName}</DialogTitle>
          </DialogHeader>
          <div className="w-full h-[70vh]">
            {viewingDoc?.fileUrl && (
              viewingDoc.fileName?.toLowerCase().endsWith('.pdf') ? (
                <iframe src={viewingDoc.fileUrl} className="w-full h-full border-0 rounded" title={viewingDoc.fileName} />
              ) : (
                <img src={viewingDoc.fileUrl} alt={viewingDoc.fileName} className="max-w-full max-h-full object-contain mx-auto" />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Document Warning Dialog */}
      <Dialog open={!!deletingDoc} onOpenChange={() => setDeletingDoc(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" /> Remove Document</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to remove <strong>{deletingDoc?.fileName}</strong>? This action cannot be undone.
          </p>
          <DialogFooter className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => setDeletingDoc(null)} data-testid="cancel-delete-doc">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteDoc} data-testid="confirm-delete-doc">Remove Document</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recent Attendance */}
      {profile.recentAttendance?.length > 0 && (
        <DataCard>
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Recent Attendance (Last 30 Days)</h3>
          <div className="flex flex-wrap gap-1.5">
            {profile.recentAttendance.map((a, i) => (
              <div
                key={i}
                className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-medium ${
                  a.status === 'present' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                  a.status === 'absent' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                  a.status === 'late' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                  'bg-slate-100 text-slate-500 dark:bg-slate-800'
                }`}
                title={`${a.date}: ${a.status}`}
              >
                {new Date(a.date).getDate()}
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 dark:bg-emerald-900/30" /> Present</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 dark:bg-red-900/30" /> Absent</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 dark:bg-amber-900/30" /> Late</span>
          </div>
        </DataCard>
      )}

      {/* Assignments */}
      <DataCard>
        <h3 className="font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Assignments</h3>
        {(() => {
          const mockAssignments = [
            { title: 'Chapter 5: Quadratic Equations', type: 'Assignment', subject: 'Mathematics', dueDate: '2026-04-15', status: 'Pending' },
            { title: 'Unit Test - Periodic Table', type: 'Test', subject: 'Chemistry', dueDate: '2026-04-12', status: 'Pending' },
            { title: 'Essay: Environmental Conservation', type: 'Homework', subject: 'English', dueDate: '2026-04-10', status: 'Submitted' },
          ];
          return (
            <div className="space-y-2">
              {mockAssignments.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg" data-testid={`profile-assignment-${i}`}>
                  <div>
                    <p className="text-sm font-medium">{a.title}</p>
                    <p className="text-xs text-muted-foreground">{a.subject} · Due: {a.dueDate}</p>
                  </div>
                  <Badge className={a.status === 'Submitted' ? 'bg-blue-500/10 text-blue-600' : 'bg-amber-500/10 text-amber-600'}>{a.status}</Badge>
                </div>
              ))}
            </div>
          );
        })()}
      </DataCard>

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer Student</DialogTitle>
            <DialogDescription>Internal (class change) or External (TC generation)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Transfer Type</Label>
              <Select value={transferForm.transferType} onValueChange={v => setTransferForm({...transferForm, transferType: v})}>
                <SelectTrigger data-testid="transfer-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal (Class/Section Change)</SelectItem>
                  <SelectItem value="external">External (TC - Transfer Certificate)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {transferForm.transferType === 'internal' && (
              <>
                <div><Label>New Class</Label>
                  <Select value={transferForm.newClass} onValueChange={v => setTransferForm({...transferForm, newClass: v})}>
                    <SelectTrigger data-testid="transfer-new-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>{allClasses.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>New Section</Label>
                  <Select value={transferForm.newSection} onValueChange={v => setTransferForm({...transferForm, newSection: v})}>
                    <SelectTrigger data-testid="transfer-new-section"><SelectValue placeholder="Select section" /></SelectTrigger>
                    <SelectContent>{allSections.map(s => <SelectItem key={s} value={s}>Section {s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}
            {transferForm.transferType === 'external' && (
              <div><Label>Destination School</Label><Input value={transferForm.destinationSchool} onChange={e => setTransferForm({...transferForm, destinationSchool: e.target.value})} placeholder="School name" data-testid="transfer-dest" /></div>
            )}
            <div><Label>Reason</Label><Textarea value={transferForm.reason} onChange={e => setTransferForm({...transferForm, reason: e.target.value})} rows={2} placeholder="Transfer reason" data-testid="transfer-reason" /></div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button onClick={handleTransfer} disabled={transferring} className="bg-brand-deep-blue" data-testid="transfer-submit">
              {transferring ? 'Processing...' : transferForm.transferType === 'external' ? 'Generate TC & Transfer' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mentor Assignment Dialog */}
      <Dialog open={mentorOpen} onOpenChange={setMentorOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Mentor</DialogTitle>
            <DialogDescription>Select a teacher as mentor for {profile.fullName}</DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Select value={selectedMentor} onValueChange={setSelectedMentor}>
              <SelectTrigger data-testid="mentor-select"><SelectValue placeholder="Select teacher" /></SelectTrigger>
              <SelectContent>
                {teachers.map(t => <SelectItem key={t.teacherId || t.userId} value={t.teacherId || t.userId}>{t.name} — {(t.subjects || []).join(', ') || 'N/A'}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setMentorOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignMentor} disabled={assigningMentor} className="bg-brand-deep-blue" data-testid="mentor-submit">
              {assigningMentor ? 'Assigning...' : 'Assign Mentor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
